// src/components/TokenomicsSection.jsx
import { useState } from "react";
import { Element } from "react-scroll";
import { motion } from "framer-motion";
import { ShoppingCart } from "lucide-react";
import TokenomicsChart from "./TokenomicsChart";
import TokenAddress from "./TokenAddress";
import WalletStatsCard from "./WalletStatsCard";
import PlayersOnline from "./PlayersOnline";
import TotalGamesPlayed from "./TotalGamesPlayed";
import WalletSelectModal from "./WalletSelectModal";

const TokenomicsSection = () => {
  const [showBuyModal, setShowBuyModal] = useState(false);

  return (
    <Element
      name="token"
      className="min-h-screen w-full flex flex-col items-center justify-center pt-60 px-6 py-24 bg-gradient-to-b from-[#40033e] via-[#2d0028] to-[#40033e] text-white scroll-mt-[120px] md:scroll-mt-[160px] relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"
          animate={{ y: [0, 50, 0], x: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl"
          animate={{ y: [0, -50, 0], x: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      <div className="max-w-6xl w-full text-center relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-center font-rubik drop-shadow-xl mb-4">
            Tokenomics
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 mx-auto rounded-full mb-4" />
          <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
            Transparent token distribution and real-time wallet tracking on BNB Chain
          </p>
        </motion.div>

        {/* Buttons Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center items-center gap-x-4 gap-y-4 mt-12 mb-20"
        >
          {/* copy-to-clipboard token address */}
          <TokenAddress />

          {/* Buy STBOX (opens wallet/DEX selector) */}
          <button
            onClick={() => setShowBuyModal(true)}
            className="w-[200px] h-[48px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F0B90B] to-[#ffcc2f] hover:from-[#ffcc2f] hover:to-[#F0B90B] rounded-full font-semibold text-black shadow-lg transition-all duration-300 hover:shadow-[0_0_24px_rgba(240,185,11,0.75)] hover:scale-105 whitespace-nowrap"
          >
            <ShoppingCart className="w-4 h-4" />
            BUY STBOX
          </button>

          {/* DEX (BNB) */}
          <a
            href="https://dexscreener.com/bsc/0xb692e9099a6b275cfc990c994f0d62cca2284444:4meme"
            target="_blank"
            rel="noopener noreferrer"
            className="w-[200px] h-[48px] flex items-center justify-center gap-2 bg-black/80 hover:bg-black rounded-full font-semibold text-white shadow-lg border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <img
              src="/images/DEX_77x77.png"
              alt="DEX"
              className="w-5 h-5"
            />
            DEXSCREENER
          </a>
        </motion.div>

        {/* Snapshot Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 text-center mb-24"
        >
          {/* Total Games Played */}
          <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-xl border border-white/10 hover:border-yellow-400/30 transition-all duration-300 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-purple-500/20 blur-2xl opacity-50" />
            <div className="pointer-events-none absolute -inset-24 opacity-20 blur-2xl bg-[radial-gradient(circle_at_30%_20%,#facc15,transparent_40%),radial-gradient(circle_at_70%_80%,#a78bfa,transparent_35%)]" />
            <div className="relative text-center">
              <h3 className="text-lg font-semibold mb-3 text-white/90">Total Games Played</h3>
              <div className="text-4xl md:text-5xl font-extrabold leading-none bg-gradient-to-r from-[#facc15] via-white to-[#f472b6] bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(250,204,21,.25)]">
                <TotalGamesPlayed />
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/40 text-xs text-emerald-300 font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                LIVE
              </div>
            </div>
          </div>

          {/* Players Online */}
          <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-xl border border-white/10 hover:border-emerald-400/30 transition-all duration-300 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 blur-2xl opacity-50" />
            <div className="pointer-events-none absolute -inset-24 opacity-20 blur-2xl bg-[radial-gradient(circle_at_20%_30%,#34d399,transparent_40%),radial-gradient(circle_at_80%_70%,#93c5fd,transparent_35%)]" />
            <div className="relative text-center">
              <h3 className="text-lg font-semibold mb-3 text-white/90">Players Online</h3>
              <div className="mt-2 flex justify-center">
                <PlayersOnline className="scale-110 md:scale-125" />
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-xl border border-white/10 hover:border-pink-400/30 transition-all duration-300 hover:scale-105 sm:col-span-2 lg:col-span-1">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-yellow-500/20 blur-2xl opacity-50" />
            <div className="pointer-events-none absolute -inset-24 opacity-20 blur-2xl bg-[radial-gradient(circle_at_25%_25%,#f472b6,transparent_40%),radial-gradient(circle_at_75%_75%,#fde68a,transparent_35%)]" />
            <div className="relative text-center">
              <h3 className="text-lg font-semibold mb-3 text-white/90">Current Status</h3>
              <div className="text-2xl md:text-3xl font-extrabold leading-none tracking-tight bg-gradient-to-r from-pink-300 to-yellow-300 bg-clip-text text-transparent">
                $STBOX is live
              </div>
              <p className="mt-3 text-xs md:text-sm text-white/70 leading-relaxed">
                Contract is live on BNB Chain. Price feed & DEX pair integration
                are coming soon while we test game and wallet flows.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/40 text-xs text-emerald-300 font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                TESTING PHASE
              </div>
            </div>
          </div>
        </motion.div>

        {/* Wallet Transparency Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-40"
        >
          {/* Section Header */}
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold font-rubik drop-shadow-xl mb-4">
              Wallet Transparency
            </h3>
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-500 to-pink-500 mx-auto rounded-full mb-4" />
            <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
              All project wallets are publicly tracked on BNB Chain for complete transparency
            </p>
          </div>

          {/* transparency notice */}
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="relative overflow-hidden bg-amber-500/10 backdrop-blur-sm border border-amber-400/40 rounded-2xl px-5 py-4 shadow-lg">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 blur-xl opacity-50" />
              <div className="relative flex gap-3 items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="h-2 w-2 rounded-full bg-amber-300 animate-pulse" />
                </div>
                <div className="text-sm text-amber-100 leading-relaxed">
                  <strong className="font-semibold">Test wallets:</strong> the two test wallets below are being used for
                  temporary funding & transparency during development. Any remaining funds will be
                  returned to the Treasury wallet after testing is finished.
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 md:gap-8 mb-24">
            {/* TEST DEV WALLET */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative overflow-hidden bg-amber-500/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-xl border-2 border-amber-300/60 text-left flex flex-col gap-3 hover:border-amber-300/80 transition-all duration-300"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 blur-xl opacity-50" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/30 border border-amber-200/50 text-xs uppercase tracking-wide text-amber-50 font-semibold w-fit mb-3">
                  TEST WALLET
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">Test DEV Wallet</h3>
                  <a
                    href="https://bscscan.com/address/0xTESTDEV000000000000000000000000000000000"
                    target="_blank"
                    rel="noreferrer"
                    className="text-amber-200 hover:text-amber-100 underline break-all text-sm transition-colors"
                  >
                    0xTESTDEV...0000
                  </a>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">
                    Used by the dev team to simulate payouts and token flows.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* TEST PLAYER WALLET */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative overflow-hidden bg-amber-500/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl shadow-xl border-2 border-amber-300/60 text-left flex flex-col gap-3 hover:border-amber-300/80 transition-all duration-300"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 blur-xl opacity-50" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/30 border border-amber-200/50 text-xs uppercase tracking-wide text-amber-50 font-semibold w-fit mb-3">
                  TEST WALLET
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">Test PLAYER Wallet</h3>
                  <a
                    href="https://bscscan.com/address/0xTESTPLAYER000000000000000000000000000000"
                    target="_blank"
                    rel="noreferrer"
                    className="text-amber-200 hover:text-amber-100 underline break-all text-sm transition-colors"
                  >
                    0xTESTPLAYER...0000
                  </a>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">
                    Used to verify in-game deposits, gameplay, and withdrawals.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* REAL WALLETS BELOW */}
            <WalletStatsCard
              label="Treasury Wallet"
              address="0x6c8896Bec65544D33EFeD362ACfa07649dA76414"
              link="https://bscscan.com/address/0x6c8896Bec65544D33EFeD362ACfa07649dA76414"
              description="This wallet handles all player rewards and receives a share of game fees. It's the engine behind game liquidity."
            />

            <WalletStatsCard
              label="Giveaway Wallet"
              address="0x2880477bc76F1A6bc14ebBf693c7b6537287D52b"
              link="https://bscscan.com/address/0x2880477bc76F1A6bc14ebBf693c7b6537287D52b"
              description="A daily or weekly prize pool for the top players. Rewards are distributed directly from here."
            />

            <WalletStatsCard
              label="Marketing Wallet"
              address="0x23b82AAE6C0112115B45FB3775c139d89A1fa350"
              link="https://bscscan.com/address/0x23b82AAE6C0112115B45FB3775c139d89A1fa350"
              description="Used to support future partnerships, influencer outreach, and visibility. Every move is tracked on-chain."
            />

            <WalletStatsCard
              label="Liquidity Wallet"
              address="0x664AFF5a7FFE34BB18Da984B0cD4AB4bF0593ad7"
              link="https://bscscan.com/address/0x664AFF5a7FFE34BB18Da984B0cD4AB4bF0593ad7"
              description="A share of losses go here. Part of the tokens are burned to drive scarcity, while the rest are locked to ensure long-term stability and fair trading."
            />
          </div>
        </motion.div>

        {/* Chart Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="w-full max-w-2xl mx-auto mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold font-rubik drop-shadow-xl mb-3">
              Token Distribution
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
          </div>

          <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8 shadow-xl">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-xl opacity-50" />
            <div className="relative">
              <TokenomicsChart />
            </div>
          </div>
        </motion.div>

        {/* Disclaimer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-3xl mx-auto mb-8"
        >
          <div className="relative overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <div className="h-1.5 w-1.5 rounded-full bg-white/40" />
              </div>
              <p className="text-xs md:text-sm text-white/60 leading-relaxed">
                <strong className="text-white/80 font-semibold">*</strong> Upon launch, the dev will pre-purchase a small amount of supply to seed rewards for
                early player achievements. This ensures real rewards from day one.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Shared wallet/DEX modal */}
      <WalletSelectModal
        open={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        onWalletChosen={(wallet) => {
          console.log("Tokenomics chose wallet:", wallet);
        }}
      />
    </Element>
  );
};

export default TokenomicsSection;
